import storefront from "../global/storefront";
import { COLLECTION_FILTERS_QUERY, SEARCH_FILTERS_QUERY } from "../global/storefront/queries";
import { buildAttributeScopedSearchQuery, escapeForQuotedValue } from "../global/storefront/search-query";

const COLOR_FAMILY_NAMESPACE = "custom";
const COLOR_FAMILY_KEY = "color_family_list";
const COLOR_FAMILY_LABEL = "color-family";

const PRICE_RANGE_CEILING = 100000;

const maxFilteredProductPages = 200;
const maxPriceBucketScanPages = 20;

const PRICE_RANGE_PRESETS = [
  { key: "under-100", label: "Under $100", min: 0, max: 100 },
  { key: "100-199", label: "$100 - $199", min: 100, max: 199 },
  { key: "200-299", label: "$200 - $299", min: 200, max: 299 },
  { key: "300-plus", label: "$300+", min: 300, max: PRICE_RANGE_CEILING },
];

const SEARCH_FILTER_GROUP_ORDER = [
  "color-family",
  "fabric",
  "size",
  "length",
  "silhouette",
  "sleeve-type",
  "neckline",
  "features",
  "delivery-time",
];

const SEARCH_URL_PARAM_NAMES = [
  COLOR_FAMILY_LABEL,
  "color",
  "fabric",
  "size",
  "length",
  "sleeve-type",
  "silhouette",
  "neckline",
  "features",
  "delivery-time",
];

const SEARCH_TAG_FILTER_DEFINITIONS = [
  { namespace: "fabric", label: "Fabric" },
  { namespace: "sleeve_type", label: "Sleeve Type" },
  { namespace: "silhouette", label: "Silhouette" },
  { namespace: "neckline", label: "Neckline" },
  { namespace: "features", label: "Features" },
];

class ApiFacets {
  constructor(root) {
    this.root = root;

    this.collectionHandle = root.dataset.collectionHandle;

    this.searchQuery = root.dataset.searchQuery || null;

    this.isAllProductsCollection =
      !this.searchQuery &&
      String(this.collectionHandle || "").trim().toLowerCase() === "all";

    this.collectionRoot = root.closest("main-collection");

    this.shopByColorRoot =
      this.collectionRoot?.querySelector("[data-shop-by-color]") ||
      document.querySelector("[data-shop-by-color]") ||
      null;

    this.selectedFilters = new Map();

    this.availableFilters = [];

    this.excludedFilterGroups = new Set();

    this.loading = false;
    this.requestToken = 0;
    this.urlStateRestored = false;

    // Progressive filtered paging state. Keep only the pages we have fetched
    // for the current filter request and fetch additional pages on demand.
    this.filteredRawNodes = [];
    this.filteredRawFilters = [];
    this.filteredNextCursor = null;
    this.filteredHasNextPage = false;
    this.filteredPageCount = 0;
    this.filteredPageLoading = false;

    this.searchColorFilteredNodes = [];

    // Price preset keys that have at least one product under the current
    // non-price selection (null = unknown, show every preset).
    this.availablePriceBucketKeys = null;
    this.priceBucketAvailabilityCache = new Map();

    // Collections: true when the Storefront API ignores price filters for the
    // shopper's market (see detectClientPriceFiltering), so price is applied
    // client-side here instead, as search already does.
    this.clientPriceFiltering = false;

    if (this.searchQuery) {
      this.neutralizeStaleClearAllLinks();

      window.addEventListener("popstate", () => this.handlePopState());
    }

    this.bindMobileApplyButton();

    this.ready = this.init();
  }

  neutralizeStaleClearAllLinks() {

    document.querySelectorAll('[data-render="clear-all-top-bar"]').forEach((element) => {
      element.innerHTML = "";
      element.hidden = true;
    });

    document.querySelectorAll('[data-render="clear-all-drawer-header"]').forEach((element) => {
      element.innerHTML = "";
    });
  }

  bindMobileApplyButton() {
    const button = document.querySelector('[data-render="mobile-apply-cta"]');

    button?.addEventListener("click", () => {
      if (!this.isMobileDrawer()) {
        return;
      }

      this.applyMobileFilters();
    });
  }

  async applyMobileFilters() {
    this.updateUrl();

    await this.load();
  }

  async init() {
    if (!this.collectionHandle && !this.searchQuery) {
      console.warn("[API FACETS] Missing collection handle or search query.");

      return;
    }

    await this.load();
  }

  handlePopState() {
    this.selectedFilters.clear();

    this.urlStateRestored = false;

    this.load();
  }

  slugify(value = "") {
    return String(value)
      .trim()
      .toLowerCase()
      .replace(/["']/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  escapeHtml(value = "") {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  escapeAttribute(value = "") {
    return this.escapeHtml(value);
  }

  valueKey(filterId, valueId) {
    return `${filterId}::${valueId}`;
  }

  filterParamName(filter) {
    if (this.isColorFamilyFilter(filter)) {
      return COLOR_FAMILY_LABEL;
    }

    if (this.isColorFilter(filter)) {
      return "color";
    }

    if (this.isPriceRangeFilter(filter)) {
      return "price";
    }

    if (filter?.label) {
      return this.slugify(filter.label);
    }

    const filterId = String(filter?.id || "");

    const pieces = filterId.split(".").filter(Boolean);

    const lastPart = pieces[pieces.length - 1] || "filter";

    return this.slugify(lastPart);
  }

  parseFilterInput(value) {
    try {
      return JSON.parse(value?.input || "{}");
    } catch (_) {
      return null;
    }
  }

  isColorInput(filterInput) {
    return Boolean(
      filterInput?.taxonomyMetafield?.namespace === "shopify" &&
        filterInput?.taxonomyMetafield?.key === "color-pattern",
    );
  }

  isColorFamilyInput(filterInput) {
    const metafield = filterInput?.productMetafield;

    return Boolean(metafield?.namespace === COLOR_FAMILY_NAMESPACE && metafield?.key === COLOR_FAMILY_KEY);
  }

  isColorFilter(filter) {
    return (filter?.values || []).some((value) => this.isColorInput(this.parseFilterInput(value)));
  }

  isColorFamilyFilter(filter) {
    const matchingInput = (filter?.values || []).some((value) => this.isColorFamilyInput(this.parseFilterInput(value)));

    return matchingInput || this.slugify(filter?.label) === COLOR_FAMILY_LABEL;
  }

  isColorFamilyFilterId(filterId) {
    return this.availableFilters.some((filter) => filter.id === filterId && this.isColorFamilyFilter(filter));
  }

  isPriceRangeFilter(filter) {
    return filter?.type === "PRICE_RANGE";
  }

  isColorFilterId(filterId) {
    return this.availableFilters.some((filter) => filter.id === filterId && this.isColorFilter(filter));
  }

  isColorFamilyEntry(entry) {
    return Boolean(entry && (this.isColorFamilyInput(entry.filter) || this.isColorFamilyFilterId(entry.filterId)));
  }

  isColorEntry(entry) {
    return Boolean(entry && (this.isColorInput(entry.filter) || this.isColorFilterId(entry.filterId)));
  }

  familySubcolorValues(colorFilter) {
    if (this.searchQuery) {
      const availableLabels = new Set(
        (this.searchColorFilteredNodes || [])
          .map((product) => this.productColorLabel(product)?.trim().toLowerCase())
          .filter(Boolean),
      );

      return (colorFilter?.values || []).filter((value) => {
        const selected = this.selectedFilters.has(this.valueKey(colorFilter.id, value.id));

        return selected || availableLabels.has((value.label || "").trim().toLowerCase());
      });
    }

    return (colorFilter?.values || []).filter((value) => {
      const selected = this.selectedFilters.has(this.valueKey(colorFilter.id, value.id));

      return value.count > 0 || selected;
    });
  }

  hasActiveColorFilter() {
    return Array.from(this.selectedFilters.values()).some(
      (entry) => this.isColorFamilyEntry(entry) || this.isColorEntry(entry),
    );
  }

  getProductTagValue(product, namespace) {
    const prefix = `${String(namespace || "").trim().toLowerCase()}:`;

    if (!prefix || prefix === ":") {
      return null;
    }

    const tag = (product?.tags || []).find((candidate) =>
      String(candidate || "").trim().toLowerCase().startsWith(prefix),
    );

    if (!tag) {
      return null;
    }

    return String(tag).slice(String(tag).indexOf(":") + 1).trim() || null;
  }

  productColorLabel(product) {

    const taggedColor = this.getProductTagValue(product, "color");

    if (taggedColor) {
      return taggedColor;
    }

    const metaobjectLabel = product?.colorPattern?.references?.nodes?.[0]?.swatchLabel?.value?.trim();

    if (metaobjectLabel) {
      return metaobjectLabel;
    }

    const title = String(product?.title || "").trim();
    const titleParts = title.split("|").map((part) => part.trim()).filter(Boolean);

    if (titleParts.length > 1) {
      return titleParts[titleParts.length - 1];
    }

    return null;
  }

  normalizeSearchColorValue(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[+_]+/g, " " )
      .replace(/\s+/g, " " );
  }

  getSelectedSpecificColorLabel(filters) {
    const colorFilter = filters.find((filter) => this.isColorFilter(filter));
    const selectedColor = colorFilter ? this.getSelectedValue(colorFilter) : null;

    return selectedColor?.label?.trim() || null;
  }

  getSearchPreferredColorLabel(products, filters) {
    if (!this.searchQuery) {
      return null;
    }

    const selectedColorLabel = this.getSelectedSpecificColorLabel(filters);

    if (selectedColorLabel) {
      return selectedColorLabel;
    }

    const normalizedSearch = this.normalizeSearchColorValue(this.searchQuery);

    if (!normalizedSearch) {
      return null;
    }

    const exactTaggedMatch = (products || []).find((product) =>
      this.normalizeSearchColorValue(this.getProductTagValue(product, "color")) === normalizedSearch,
    );

    if (exactTaggedMatch) {
      return this.getProductTagValue(exactTaggedMatch, "color");
    }

    const exactLabelMatch = (products || []).find((product) =>
      this.normalizeSearchColorValue(this.productColorLabel(product)) === normalizedSearch,
    );

    return exactLabelMatch ? this.productColorLabel(exactLabelMatch) : null;
  }

  prioritizeSearchColorProducts(products, filters) {
    if (!this.searchQuery || !Array.isArray(products) || products.length < 2) {
      return products;
    }

    const preferredColor = this.getSearchPreferredColorLabel(products, filters);
    const normalizedPreferred = this.normalizeSearchColorValue(preferredColor);

    if (!normalizedPreferred) {
      return products;
    }

    const preferred = [];
    const remaining = [];

    products.forEach((product) => {
      const matches = this.normalizeSearchColorValue(this.productColorLabel(product)) === normalizedPreferred;

      (matches ? preferred : remaining).push(product);
    });

    return preferred.length ? [...preferred, ...remaining] : products;
  }

  productColorSwatch(product) {
    const node = product?.colorPattern?.references?.nodes?.[0];

    if (!node) {
      return null;
    }

    const color = node.swatchColor?.value || null;

    const imageUrl = node.swatchImage?.reference?.image?.url || null;

    return {
      color,

      image: imageUrl ? { image: { url: imageUrl }, previewImage: { url: imageUrl } } : null,
    };
  }

  augmentColorFilterWithClientMatches(filters) {
    const colorFilter = filters.find((filter) => this.isColorFilter(filter));

    if (!colorFilter) {
      return;
    }

    colorFilter.values = colorFilter.values || [];

    const existingLabels = new Set(colorFilter.values.map((value) => (value.label || "").trim().toLowerCase()));

    const entriesByLabel = new Map();

    (this.searchColorFilteredNodes || []).forEach((product) => {
      const label = this.productColorLabel(product);

      const key = label?.trim().toLowerCase();

      if (!key || existingLabels.has(key)) {
        return;
      }

      const entry = entriesByLabel.get(key) || { label, count: 0, swatch: this.productColorSwatch(product) };

      entry.count += 1;

      entriesByLabel.set(key, entry);
    });

    entriesByLabel.forEach((entry, key) => {
      colorFilter.values.push({
        id: `client-color::${key}`,

        label: entry.label,

        count: entry.count,

        input: JSON.stringify({
          taxonomyMetafield: { namespace: "shopify", key: "color-pattern", value: `client-color::${key}` },
        }),

        swatch: entry.swatch,

        image: null,
      });
    });
  }

  getActiveColorFamily(filters) {
    const familyFilter = filters.find((filter) => this.isColorFamilyFilter(filter));

    const selectedFamily = familyFilter ? this.getSelectedValue(familyFilter) : null;

    if (!selectedFamily) {
      return null;
    }

    const metaobjectId = this.parseFilterInput(selectedFamily)?.productMetafield?.value || null;

    return { label: selectedFamily.label || null, metaobjectId };
  }

  hasSpecificColorSelected(filters) {
    const colorFilter = filters.find((filter) => this.isColorFilter(filter));

    return Boolean(colorFilter && this.getSelectedValue(colorFilter));
  }

  applyColorFamilyOrder(products, filters) {
    const activeColorFamily = this.getActiveColorFamily(filters);

    if (!activeColorFamily) {
      return products;
    }

    const colorFilter = filters.find((filter) => this.isColorFilter(filter));

    const selectedColor = colorFilter ? this.getSelectedValue(colorFilter) : null;

    const selectedColorLabel = selectedColor?.label?.trim().toLowerCase() || null;

    if (!selectedColorLabel) {
      return products;
    }

    return [...products].sort((a, b) => {
      const aIsSelected = this.productColorLabel(a)?.trim().toLowerCase() === selectedColorLabel;

      const bIsSelected = this.productColorLabel(b)?.trim().toLowerCase() === selectedColorLabel;

      if (aIsSelected && !bIsSelected) return -1;

      if (bIsSelected && !aIsSelected) return 1;

      return 0;
    });
  }

  filterProductsByActiveColor(products, filters) {
    const activeColorFamily = this.getActiveColorFamily(filters);

    if (!activeColorFamily) {
      return products;
    }

    let filtered = products;

    if (activeColorFamily.metaobjectId) {
      filtered = filtered.filter((product) =>
        (product?.colorFamilyList?.references?.nodes || []).some(
          (node) => node?.id === activeColorFamily.metaobjectId,
        ),
      );
    }

    const colorFilter = filters.find((filter) => this.isColorFilter(filter));

    const selectedColor = colorFilter ? this.getSelectedValue(colorFilter) : null;

    const selectedColorLabel = selectedColor?.label?.trim().toLowerCase() || null;

    if (!selectedColorLabel) {
      return filtered;
    }

    return filtered.filter((product) => this.productColorLabel(product)?.trim().toLowerCase() === selectedColorLabel);
  }

  filterProductsByActivePrice(products) {
    const priceEntry = Array.from(this.selectedFilters.values()).find((entry) => this.isPriceRangeEntry(entry));

    if (!priceEntry) {
      return products;
    }

    const { min, max } = priceEntry.filter.price;

    return products.filter((product) => {
      const amount = Number(product?.priceRange?.minVariantPrice?.amount);

      if (!Number.isFinite(amount)) {
        return false;
      }

      const aboveMin = min === undefined || min === null || amount >= min;

      const belowMax = max === undefined || max === null || amount <= max;

      return aboveMin && belowMax;
    });
  }

  setExcludedFilterGroups(collection) {
    this.excludedFilterGroups = new Set();

    let names;

    try {
      names = JSON.parse(collection?.excludeFilterGroups?.value || "[]");
    } catch (error) {
      console.error("[API FACETS] Invalid exclude_filter_groups value:", collection?.excludeFilterGroups?.value);

      return;
    }

    (Array.isArray(names) ? names : []).forEach((name) => {
      const key = this.slugify(name);

      if (key) {
        this.excludedFilterGroups.add(key);
      }
    });
  }

  filterGroupKey(filter) {
    if (this.isColorFamilyFilter(filter) || this.isColorFilter(filter)) {
      return "color";
    }

    return this.slugify(filter?.label || "");
  }

  isFilterGroupExcluded(filter) {
    const key = this.filterGroupKey(filter);

    return Boolean(key) && this.excludedFilterGroups.has(key);
  }

  searchFilterCatalogKey(filter) {
    if (this.isColorFamilyFilter(filter)) {
      return "color-family";
    }

    if (this.isColorFilter(filter)) {
      return "color";
    }

    return this.filterGroupKey(filter);
  }

  humanizeMetaobjectHandle(handle = "") {
    return String(handle || "")
      .trim()
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  makeSyntheticSearchColorFamilyFilter(products = [], serverFilters = []) {
    const serverValuesById = new Map();

    (serverFilters || []).forEach((filter) => {
      if (!this.isColorFamilyFilter(filter)) {
        return;
      }

      (filter.values || []).forEach((value) => {
        const metaobjectId = this.parseFilterInput(value)?.productMetafield?.value;

        if (metaobjectId) {
          serverValuesById.set(metaobjectId, value);
        }
      });
    });

    const entries = new Map();

    (products || []).forEach((product) => {
      const seenForProduct = new Set();

      (product?.colorFamilyList?.references?.nodes || []).forEach((node) => {
        const id = node?.id;

        if (!id || seenForProduct.has(id)) {
          return;
        }

        seenForProduct.add(id);

        const serverValue = serverValuesById.get(id);
        const metaobjectName = node?.colorFamilyName?.value?.trim();
        const fallbackLabel = this.humanizeMetaobjectHandle(node?.handle);
        const label = metaobjectName || serverValue?.label || fallbackLabel;

        if (!label) {
          return;
        }

        const rawPosition = Number.parseInt(node?.position?.value, 10);
        const position = Number.isFinite(rawPosition) ? rawPosition : Number.MAX_SAFE_INTEGER;
        const imageUrl = node?.swatchImage?.reference?.image?.url || null;
        const swatchColor = node?.swatchColor?.value || null;

        const current = entries.get(id) || {
          id,
          label,
          count: 0,
          position,
          imageUrl,
          swatchColor,
          serverValue,
        };

        current.count += 1;

        if (position < current.position) current.position = position;
        if (!current.imageUrl && imageUrl) current.imageUrl = imageUrl;
        if (!current.swatchColor && swatchColor) current.swatchColor = swatchColor;
        if (!current.serverValue && serverValue) current.serverValue = serverValue;

        entries.set(id, current);
      });
    });

    if (!entries.size) {
      return null;
    }

    const values = Array.from(entries.values())
      .sort((a, b) => {
        if (a.position !== b.position) {
          return a.position - b.position;
        }

        return a.label.localeCompare(b.label);
      })
      .map((entry) => {
        const serverValue = entry.serverValue;

        const image = entry.imageUrl
          ? {
              image: { url: entry.imageUrl },
              previewImage: { url: entry.imageUrl },
            }
          : serverValue?.image || null;

        const swatch = entry.swatchColor
          ? {
              ...(serverValue?.swatch || {}),
              color: entry.swatchColor,
              image: entry.imageUrl
                ? {
                    image: { url: entry.imageUrl },
                    previewImage: { url: entry.imageUrl },
                  }
                : serverValue?.swatch?.image || null,
            }
          : serverValue?.swatch || null;

        return {
          id: `search-client-color-family::${this.slugify(entry.label)}`,
          label: entry.label,
          count: entry.count,
          input:
            serverValue?.input ||
            JSON.stringify({
              productMetafield: {
                namespace: COLOR_FAMILY_NAMESPACE,
                key: COLOR_FAMILY_KEY,
                value: entry.id,
              },
            }),
          image,
          swatch,

          position: entry.position,
        };
      });

    return {
      id: "search-client-color-family",
      label: "Color Family",
      type: "LIST",
      presentation: "SWATCH",
      values,
    };
  }

  makeSyntheticSearchTagFilter(namespace, label, products = []) {
    const counts = new Map();
    const labels = new Map();
    const prefix = `${namespace.toLowerCase()}:`;

    (products || []).forEach((product) => {
      (product?.tags || []).forEach((rawTag) => {
        const tag = String(rawTag || "").trim();
        const lower = tag.toLowerCase();

        if (!lower.startsWith(prefix)) {
          return;
        }

        const value = tag.slice(tag.indexOf(":") + 1).trim();
        const key = value.toLowerCase();

        if (!key) {
          return;
        }

        labels.set(key, labels.get(key) || value);
        counts.set(key, (counts.get(key) || 0) + 1);
      });
    });

    const values = Array.from(counts.entries())
      .map(([key, count]) => {
        const valueLabel = labels.get(key) || key;
        const fullTag = `${namespace}:${valueLabel}`;

        return {
          id: `search-tag::${namespace}::${this.slugify(valueLabel)}`,
          label: valueLabel,
          count,
          input: JSON.stringify({ tag: fullTag }),
          image: null,
          swatch: null,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    if (!values.length) {
      return null;
    }

    return {
      id: `search-tag::${namespace}`,
      label,
      type: "LIST",
      presentation: "TEXT",
      values,
    };
  }

  makeSyntheticSearchColorFilter(products = []) {
    const entries = new Map();

    (products || []).forEach((product) => {
      const label = this.productColorLabel(product);
      const key = label?.trim().toLowerCase();

      if (!key) {
        return;
      }

      const current = entries.get(key) || {
        label,
        count: 0,
        swatch: this.productColorSwatch(product),
      };

      current.count += 1;
      current.swatch ||= this.productColorSwatch(product);
      entries.set(key, current);
    });

    if (!entries.size) {
      return null;
    }

    return {
      id: "search-client-color",
      label: "Color",
      type: "LIST",
      presentation: "SWATCH",
      values: Array.from(entries.entries())
        .map(([key, entry]) => ({
          id: `search-client-color::${this.slugify(key)}`,
          label: entry.label,
          count: entry.count,

          input: JSON.stringify({
            taxonomyMetafield: {
              namespace: "shopify",
              key: "color-pattern",
              value: `search-client-color::${this.slugify(key)}`,
            },
          }),
          image: null,
          swatch: entry.swatch,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    };
  }

  cloneSearchFilter(filter) {
    return {
      ...filter,
      values: (filter?.values || []).map((value) => ({ ...value })),
    };
  }

  buildStableSearchFilters(serverFilters = [], products = []) {
    if (!this.searchQuery) {
      return serverFilters;
    }

    const tagGroupKeys = new Set([
      "color",
      ...SEARCH_TAG_FILTER_DEFINITIONS.map((definition) => this.slugify(definition.label)),
    ]);

    const nativeFilters = (serverFilters || [])
      .filter((filter) => {
        const key = this.filterGroupKey(filter);

        return !tagGroupKeys.has(key);
      })
      .map((filter) => this.cloneSearchFilter(filter));

    const synthetic = [];

    const colorFamilyFilter = this.makeSyntheticSearchColorFamilyFilter(products, serverFilters);
    const colorFilter = this.makeSyntheticSearchColorFilter(products);

    if (colorFamilyFilter) {
      synthetic.push(colorFamilyFilter);
    }

    if (colorFilter) {
      synthetic.push(colorFilter);
    }

    SEARCH_TAG_FILTER_DEFINITIONS.forEach(({ namespace, label }) => {
      const filter = this.makeSyntheticSearchTagFilter(namespace, label, products);

      if (filter) {
        synthetic.push(filter);
      }
    });

    return [...synthetic, ...nativeFilters]
      .map((filter) => this.cloneSearchFilter(filter))
      .map((filter) => this.pruneSearchFilterValues(filter))
      .filter(Boolean);
  }

  pruneSearchFilterValues(filter) {
    if (!this.searchQuery || !filter) {
      return filter;
    }

    if (this.isPriceRangeFilter(filter)) {
      return filter;
    }

    const values = (filter.values || []).filter((value) => Number(value?.count || 0) > 0);

    if (!values.length) {
      return null;
    }

    return {
      ...filter,
      values,
    };
  }

  getVisibleFilters(filters = []) {
    return filters.filter((filter) => {
      if (this.isFilterGroupExcluded(filter)) {
        return false;
      }

      if (this.searchQuery) {
        const groupKey = this.filterGroupKey(filter);

        if (groupKey === "price") {
          return false;
        }
      }

      return true;
    });
  }

  getFiltersArray() {
    return Array.from(this.selectedFilters.values())
      .filter((entry) => {
        if (!this.searchQuery) {
          return !(this.clientPriceFiltering && this.isPriceRangeEntry(entry));
        }

        if (entry.queryClause) {
          return false;
        }

        if (this.isColorFamilyEntry(entry) || this.isColorEntry(entry)) {
          return false;
        }

        if (this.isPriceRangeEntry(entry)) {
          return false;
        }

        return true;
      })
      .map((entry) => entry.filter)
      .filter(Boolean);
  }

  isPriceRangeEntry(entry) {
    return Boolean(entry?.filter?.price);
  }

  buildSearchFilterQueryClause(filter, value, parsed) {
    if (!filter || !parsed) {
      return null;
    }

    if (parsed.tag) {
      return `tag:"${escapeForQuotedValue(parsed.tag)}"`;
    }

    return null;
  }

  buildSearchFilterQueryGroup(filter) {
    if (this.isColorFamilyFilter(filter)) {
      return "color-family";
    }

    if (this.isColorFilter(filter)) {
      return "color-value";
    }

    return this.filterGroupKey(filter);
  }

  buildSelectedFiltersQuery() {
    const groups = new Map();

    this.selectedFilters.forEach((entry) => {
      if (!entry.queryClause) {
        return;
      }

      if (!groups.has(entry.queryGroup)) {
        groups.set(entry.queryGroup, new Set());
      }

      groups.get(entry.queryGroup).add(entry.queryClause);
    });

    return Array.from(groups.values())
      .map((clauseSet) => {
        const clauses = Array.from(clauseSet);

        return clauses.length > 1 ? `(${clauses.join(" OR ")})` : clauses[0];
      })
      .join(" AND ");
  }

  getSelectedValue(filter) {
    return (filter?.values || []).find((value) => this.selectedFilters.has(this.valueKey(filter.id, value.id)));
  }

  selectedCountForFilter(filter) {
    return (filter.values || []).filter((value) => this.selectedFilters.has(this.valueKey(filter.id, value.id))).length;
  }

  getValueImageUrl(value) {
    return value?.image?.image?.url || value?.image?.previewImage?.url || null;
  }

  getValueSwatchImageUrl(value) {
    return value?.swatch?.image?.image?.url || value?.swatch?.image?.previewImage?.url || null;
  }

  isMobileDrawer() {
    return window.matchMedia("(max-width: 1023px)").matches;
  }

  buildFiltersRequest(first = 250, after = null) {
    if (this.searchQuery) {

      const baseQuery = buildAttributeScopedSearchQuery(this.searchQuery);

      const selectedFiltersQuery = this.buildSelectedFiltersQuery();

      const query = selectedFiltersQuery ? `(${baseQuery}) AND ${selectedFiltersQuery}` : baseQuery;

      return {
        query: SEARCH_FILTERS_QUERY,

        variables: {
          query,

          first,

          after,

          filters: this.getFiltersArray(),
        },
      };
    }

    if (this.isAllProductsCollection) {
      return {
        query: SEARCH_FILTERS_QUERY,

        variables: {
          query: "*",

          first,

          after,

          filters: this.getFiltersArray(),
        },
      };
    }

    return {
      query: COLLECTION_FILTERS_QUERY,

      variables: {
        handle: this.collectionHandle,

        first,

        after,

        filters: this.getFiltersArray(),
      },
    };
  }

  async fetchFilteredProductsPage(after = null) {
    const response = await storefront.request(this.buildFiltersRequest(250, after));

    if (response?.errors?.length) {
      console.error("[API FACETS] GraphQL errors:", response.errors);

      return null;
    }

    const page = this.normalizeFiltersResponse(response);

    if (!page) {
      console.error("[API FACETS] Invalid Storefront response:", {
        mode: this.searchQuery
          ? "search"
          : this.isAllProductsCollection
            ? "all-products"
            : "collection",
        query: this.searchQuery || null,
        collectionHandle: this.collectionHandle || null,
        response,
      });

      return null;
    }

    return page;
  }

  // Search, and collections filtering price client-side: walks every cursor
  // page of the current selection, applying the same client-side filtering the
  // grid uses (search: color + price; collections: price), so the result
  // count reflects exactly what the shopper is filtering. Returns false
  // if the selection changed mid-scan (the pages would be a mix of two queries).
  async collectSearchCountProducts({ onPage } = {}) {
    const token = this.requestToken;
    let after = null;
    let pageCount = 0;

    do {
      const page = await this.fetchFilteredProductsPage(after);

      if (token !== this.requestToken || !page) {
        return false;
      }

      pageCount += 1;

      let nodes = page.products?.nodes || [];

      if (this.searchQuery) {
        nodes = this.filterProductsByActiveColor(nodes, this.availableFilters);
      }

      nodes = this.filterProductsByActivePrice(nodes);

      const pageInfo = page.products?.pageInfo;

      after = pageInfo?.hasNextPage && pageCount < maxFilteredProductPages ? pageInfo.endCursor || null : null;

      await onPage?.(nodes, Boolean(after));

      if (token !== this.requestToken) {
        return false;
      }
    } while (after);

    return true;
  }

  async loadNextFilteredPage() {
    if (
      this.filteredPageLoading ||
      !this.filteredHasNextPage ||
      !this.filteredNextCursor
    ) {
      return false;
    }

    const token = this.requestToken;
    const cursor = this.filteredNextCursor;

    this.filteredPageLoading = true;

    try {
      const page = await this.fetchFilteredProductsPage(cursor);

      if (token !== this.requestToken || !page) {
        return false;
      }

      const pageProducts = page.products || {};

      this.filteredRawNodes.push(...(pageProducts.nodes || []));
      this.filteredRawFilters = pageProducts.filters || this.filteredRawFilters;
      this.filteredPageCount += 1;

      this.filteredHasNextPage = Boolean(pageProducts.pageInfo?.hasNextPage);
      this.filteredNextCursor = this.filteredHasNextPage
        ? pageProducts.pageInfo?.endCursor || null
        : null;

      if (this.filteredHasNextPage && !this.filteredNextCursor) {
        this.filteredHasNextPage = false;
      }

      if (
        this.filteredHasNextPage &&
        this.filteredPageCount >= maxFilteredProductPages
      ) {
        console.warn(
          `[API FACETS] Filtered product fetch hit the ${maxFilteredProductPages}-page safety cap; results beyond ${
            maxFilteredProductPages * 250
          } products are truncated.`,
        );

        this.filteredHasNextPage = false;
        this.filteredNextCursor = null;
      }

      let nodes = [...this.filteredRawNodes];

      if (this.searchQuery) {
        nodes = this.filterProductsByActiveColor(nodes, this.availableFilters);
        this.searchColorFilteredNodes = nodes;
        this.augmentColorFilterWithClientMatches(this.availableFilters);
        this.render(this.availableFilters);
        nodes = this.filterProductsByActivePrice(nodes);
        nodes = this.prioritizeSearchColorProducts(nodes, this.availableFilters);
      } else if (this.clientPriceFiltering) {
        nodes = this.filterProductsByActivePrice(nodes);
      }

      await this.dispatchProducts(
        {
          filters: this.filteredRawFilters,
          nodes,
          pageInfo: {
            hasNextPage: this.filteredHasNextPage,
            endCursor: this.filteredNextCursor,
          },
        },
        {
          isBackgroundCompletion: true,
          requestToken: token,
        },
      );

      return true;
    } finally {
      this.filteredPageLoading = false;
    }
  }

  async loadRemainingFilteredPages(token, after, rawNodes, rawFilters) {
    let hasNextPage = Boolean(after);

    let pageCount = 1;

    while (hasNextPage) {
      if (token !== this.requestToken) {
        return;
      }

      pageCount += 1;

      const page = await this.fetchFilteredProductsPage(after);

      if (token !== this.requestToken || !page) {
        return;
      }

      rawNodes.push(...(page.products?.nodes || []));

      hasNextPage = Boolean(page.products?.pageInfo?.hasNextPage);
      after = page.products?.pageInfo?.endCursor || null;

      if (hasNextPage && !after) {
        break;
      }

      if (hasNextPage && pageCount >= maxFilteredProductPages) {
        console.warn(
          `[API FACETS] Filtered product fetch hit the ${maxFilteredProductPages}-page safety cap; results beyond ${
            maxFilteredProductPages * 250
          } products are truncated.`,
        );

        break;
      }
    }

    if (token !== this.requestToken) {
      return;
    }

    let nodes = rawNodes;

    if (this.searchQuery) {
      nodes = this.filterProductsByActiveColor(nodes, this.availableFilters);

      this.searchColorFilteredNodes = nodes;

      this.augmentColorFilterWithClientMatches(this.availableFilters);

      this.render(this.availableFilters);

      nodes = this.filterProductsByActivePrice(nodes);
      nodes = this.prioritizeSearchColorProducts(nodes, this.availableFilters);
    } else if (this.clientPriceFiltering) {
      nodes = this.filterProductsByActivePrice(nodes);
    }

    this.dispatchProducts(
      { filters: rawFilters, nodes, pageInfo: { hasNextPage, endCursor: after } },
      { isBackgroundCompletion: true },
    );
  }

  normalizeFiltersResponse(response) {
    if (this.searchQuery || this.isAllProductsCollection) {
      const search = response?.data?.search;

      if (!search) {
        console.error(
          this.isAllProductsCollection
            ? "[API FACETS] All-products data missing from Storefront response:"
            : "[API FACETS] Search data missing from Storefront response:",
          response,
        );

        return null;
      }

      return {
        excludeFilterGroups: null,

        products: {
          filters: search.productFilters || [],

          nodes: (search.edges || []).map((edge) => edge?.node).filter(Boolean),

          pageInfo: search.pageInfo || null,
        },
      };
    }

    const collection = response?.data?.collection;

    if (!collection) {
      console.error("[API FACETS] Collection data missing from Storefront response:", response);
      return null;
    }

    return collection;
  }

  // Outside the shop's primary market (e.g. CAD for a Canadian shopper) the
  // Storefront API leaves PRICE_RANGE out of the collection's filters and
  // silently ignores price filters — every range returns every product. Its
  // absence from an unfiltered response is the signal to filter price here.
  detectClientPriceFiltering(rawFilters = []) {
    if (this.searchQuery) {
      return;
    }

    this.clientPriceFiltering = !rawFilters.some((filter) => this.isPriceRangeFilter(filter));
  }

  hasActivePriceSelection() {
    return Array.from(this.selectedFilters.values()).some((entry) => this.isPriceRangeEntry(entry));
  }

  // Collections filtering price client-side: which presets have a product,
  // from the prices of every product under the current non-price selection
  // (the same set the grid filters, since getFiltersArray() omits price).
  async fetchClientPriceBuckets(firstPage) {
    const cacheKey = `client::${JSON.stringify(this.getFiltersArray())}`;

    if (this.priceBucketAvailabilityCache.has(cacheKey)) {
      return this.priceBucketAvailabilityCache.get(cacheKey);
    }

    const request = (async () => {
      const nodes = [...(firstPage?.nodes || [])];
      let pageInfo = firstPage?.pageInfo;
      let pageCount = 1;

      while (pageInfo?.hasNextPage && pageInfo.endCursor && pageCount < maxPriceBucketScanPages) {
        const page = await this.fetchFilteredProductsPage(pageInfo.endCursor);

        if (!page) {
          throw new Error("Price bucket scan failed");
        }

        nodes.push(...(page.products?.nodes || []));
        pageInfo = page.products?.pageInfo;
        pageCount += 1;
      }

      const amounts = nodes
        .map((product) => Number(product?.priceRange?.minVariantPrice?.amount))
        .filter(Number.isFinite);

      return new Set(
        PRICE_RANGE_PRESETS.filter((preset) =>
          amounts.some((amount) => amount >= preset.min && amount <= preset.max),
        ).map((preset) => preset.key),
      );
    })().catch((error) => {
      console.error("[API FACETS] Failed checking price ranges:", error);
      this.priceBucketAvailabilityCache.delete(cacheKey);

      return null;
    });

    this.priceBucketAvailabilityCache.set(cacheKey, request);

    return request;
  }

  // Collections only (search hides the price filter). Asks the Storefront API,
  // in one request, whether each price preset has any product under the
  // current non-price selection, so empty ranges (e.g. $300+ on a collection
  // topping out at $250) aren't offered. Price selections are left out of the
  // probe so shoppers can still switch to another range that has products.
  fetchAvailablePriceBuckets() {
    if (this.searchQuery || this.clientPriceFiltering) {
      return Promise.resolve(null);
    }

    const baseFilters = Array.from(this.selectedFilters.values())
      .filter((entry) => !this.isPriceRangeEntry(entry))
      .map((entry) => entry.filter)
      .filter(Boolean);

    const cacheKey = JSON.stringify(baseFilters);

    if (this.priceBucketAvailabilityCache.has(cacheKey)) {
      return this.priceBucketAvailabilityCache.get(cacheKey);
    }

    const variables = {};
    const declarations = [];
    const selections = [];

    PRICE_RANGE_PRESETS.forEach((preset, index) => {
      variables[`f${index}`] = [...baseFilters, { price: { min: preset.min, max: preset.max } }];
      declarations.push(`$f${index}: [ProductFilter!]`);

      selections.push(
        this.isAllProductsCollection
          ? `b${index}: search(query: "*", types: [PRODUCT], productFilters: $f${index}, first: 1) { totalCount }`
          : `b${index}: products(first: 1, filters: $f${index}) { nodes { id } }`,
      );
    });

    let query;

    if (this.isAllProductsCollection) {
      query = `query PriceBucketAvailability(${declarations.join(", ")}) { ${selections.join(" ")} }`;
    } else {
      variables.handle = this.collectionHandle;
      query = `query PriceBucketAvailability($handle: String!, ${declarations.join(", ")}) {
        collection(handle: $handle) { ${selections.join(" ")} }
      }`;
    }

    const request = storefront
      .request({ query, variables })
      .then((response) => {
        const data = this.isAllProductsCollection ? response?.data : response?.data?.collection;

        if (response?.errors?.length || !data) {
          throw new Error("Price bucket availability query failed");
        }

        return new Set(
          PRICE_RANGE_PRESETS.filter((preset, index) => {
            const connection = data[`b${index}`];

            return this.isAllProductsCollection
              ? Number(connection?.totalCount || 0) > 0
              : Boolean(connection?.nodes?.length);
          }).map((preset) => preset.key),
        );
      })
      .catch((error) => {
        console.error("[API FACETS] Failed checking price ranges:", error);
        this.priceBucketAvailabilityCache.delete(cacheKey);

        return null;
      });

    this.priceBucketAvailabilityCache.set(cacheKey, request);

    return request;
  }

  // Shopify leaves the PRICE_RANGE filter out of the response outside the
  // primary market (e.g. a Canadian shopper in CAD), though price filtering
  // itself still works there. Add it back so collections always offer Price;
  // fetchAvailablePriceBuckets() then hides it if no range has products.
  // Placed before Delivery Time, where Shopify returns it in the primary market.
  withPriceRangeFilter(filters = []) {
    if (filters.some((filter) => this.isPriceRangeFilter(filter))) {
      return filters;
    }

    const priceFilter = { id: "filter.v.price", label: "Price", type: "PRICE_RANGE", values: [] };
    const deliveryIndex = filters.findIndex((filter) => this.filterGroupKey(filter) === "delivery-time");
    const result = [...filters];

    result.splice(deliveryIndex === -1 ? result.length : deliveryIndex, 0, priceFilter);

    return result;
  }

  // Presets to offer: ones with products, plus any the shopper already has
  // checked (so it can still be unchecked). Every preset when unknown.
  visiblePriceBucketPresets(filter) {
    return PRICE_RANGE_PRESETS.filter(
      (preset) =>
        !this.availablePriceBucketKeys ||
        this.availablePriceBucketKeys.has(preset.key) ||
        this.selectedFilters.has(this.valueKey(filter.id, `price-bucket-${preset.key}`)),
    );
  }

  async load() {
    const token = ++this.requestToken;

    // Runs alongside the products request below; awaited before render.
    const priceBucketsRequest = this.fetchAvailablePriceBuckets();

    // Tell the collection immediately that a filter/sort request has started.
    // This removes interstitials before the network request, rather than after
    // filtered products return.
    this.root.dispatchEvent(
      new CustomEvent("api-facets:loading", {
        bubbles: true,
        detail: {
          hasActiveFilters: this.selectedFilters.size > 0,
          requestToken: token,
        },
      }),
    );

    this.loading = true;

    this.root.classList.add("is-loading");
    this.collectionRoot?.beginSynchronizedUpdate?.();

    try {
      const collection = await this.fetchFilteredProductsPage();

      if (token !== this.requestToken) {
        return;
      }

      if (!collection) {
        return;
      }

      this.setExcludedFilterGroups(collection);

      const products = collection.products;

      const rawNodes = products.nodes || [];
      const rawFilters = products.filters || [];
      const nextPageAfter = products.pageInfo?.hasNextPage ? products.pageInfo?.endCursor || null : null;

      if (this.selectedFilters.size === 0) {
        this.detectClientPriceFiltering(rawFilters);
      }

      this.filteredRawNodes = [...rawNodes];
      this.filteredRawFilters = rawFilters;
      this.filteredNextCursor = nextPageAfter;
      this.filteredHasNextPage = Boolean(nextPageAfter);
      this.filteredPageCount = 1;

      const searchAwareFilters = this.searchQuery
        ? this.buildStableSearchFilters(products.filters || [], rawNodes)
        : this.withPriceRangeFilter(products.filters || []);

      const filters = this.getVisibleFilters(searchAwareFilters);

      this.availableFilters = filters;

      if (!this.urlStateRestored) {
        this.restoreFromUrlUsingFilters(filters);

        this.urlStateRestored = true;

        if (this.selectedFilters.size > 0) {
          this.loading = false;

          this.root.classList.remove("is-loading");

          await this.load();

          return;
        }
      }

      if (this.searchQuery) {
        products.nodes = this.filterProductsByActiveColor(products.nodes || [], filters);

        this.searchColorFilteredNodes = products.nodes;

        this.augmentColorFilterWithClientMatches(filters);
      }

      if (!this.searchQuery) {
        this.availablePriceBucketKeys = this.clientPriceFiltering
          ? await this.fetchClientPriceBuckets(products)
          : await priceBucketsRequest;

        if (token !== this.requestToken) {
          return;
        }
      }

      this.render(filters);

      if (this.searchQuery) {
        this.root.classList.remove("is-loading");
      }

      if (this.searchQuery) {
        products.nodes = this.filterProductsByActivePrice(products.nodes);

        products.nodes = this.prioritizeSearchColorProducts(products.nodes, filters);
      } else if (this.clientPriceFiltering) {
        products.nodes = this.filterProductsByActivePrice(products.nodes);
      }

      await this.dispatchProducts(products, { requestToken: token });

      // Search: color/price filtering is client-side per fetched page, so the
      // first page can leave fewer cards than one Load More chunk even though
      // later pages have more. Fill the first chunk before settling, so the
      // grid and Load More button reflect the real result set.
      if (this.searchQuery || (this.clientPriceFiltering && this.hasActivePriceSelection())) {
        const firstChunkSize = this.collectionRoot?.getInitialProductLimit?.() || 20;

        while (
          token === this.requestToken &&
          this.filteredHasNextPage &&
          (this.collectionRoot?.allCards?.().length || 0) < firstChunkSize
        ) {
          const loaded = await this.loadNextFilteredPage();

          if (!loaded) break;
        }
      }

      // Do not exhaust every filtered cursor page in the background. The first
      // page is enough to render the initial result set; Load More requests
      // additional pages through loadNextFilteredPage() only when needed.
    } catch (error) {
      console.error("[API FACETS] Failed loading filters:", error);
    } finally {
      if (token === this.requestToken) {
        this.loading = false;

        this.root.classList.remove("is-loading");
      }

      this.collectionRoot?.endSynchronizedUpdate?.();
    }
  }

  restoreFromUrlUsingFilters(filters) {
    const params = new URLSearchParams(window.location.search);

    filters.forEach((filter) => {
      if (this.isPriceRangeFilter(filter)) {
        this.restorePriceRangeFromUrl(filter, params);

        return;
      }

      const paramName = this.filterParamName(filter);

      const requestedValues = params.getAll(paramName);

      if (!requestedValues.length) {
        return;
      }

      (filter.values || []).forEach((value) => {
        const urlValue = this.slugify(value.label);

        if (!requestedValues.includes(urlValue)) {
          return;
        }

        let parsed;

        try {
          parsed = JSON.parse(value.input);
        } catch (error) {
          console.error("[API FACETS] Could not restore filter:", value.label, error);

          return;
        }

        const key = this.valueKey(filter.id, value.id);

        if (this.isColorFamilyFilter(filter)) {
          Array.from(this.selectedFilters.entries()).forEach(([existingKey, entry]) => {
            if (this.isColorFamilyEntry(entry)) {
              this.selectedFilters.delete(existingKey);
            }
          });
        } else if (this.isColorInput(parsed)) {
          Array.from(this.selectedFilters.entries()).forEach(([existingKey, entry]) => {
            if (this.isColorEntry(entry)) {
              this.selectedFilters.delete(existingKey);
            }
          });
        }

        this.selectedFilters.set(key, {
          filter: parsed,

          filterId: filter.id,

          valueId: value.id,

          urlParam: paramName,

          urlValue,

          queryGroup: this.searchQuery ? this.buildSearchFilterQueryGroup(filter) : null,

          queryClause: this.searchQuery ? this.buildSearchFilterQueryClause(filter, value, parsed) : null,
        });
      });
    });
  }

  restorePriceRangeFromUrl(filter, params) {
    const checkedKeys = params
      .getAll("price")
      .filter((key) => PRICE_RANGE_PRESETS.some((preset) => preset.key === key));

    if (!checkedKeys.length) {
      return;
    }

    this.applyPriceBuckets(filter.id, checkedKeys);
  }

  applyPriceBuckets(filterId, checkedKeys) {
    Array.from(this.selectedFilters.keys())
      .filter((key) => key.startsWith(`${filterId}::price-bucket-`))
      .forEach((key) => this.selectedFilters.delete(key));

    const checkedPresets = PRICE_RANGE_PRESETS.filter((preset) => checkedKeys.includes(preset.key));

    if (!checkedPresets.length) {
      return;
    }

    const combined = {
      min: Math.min(...checkedPresets.map((preset) => preset.min)),

      max: Math.max(...checkedPresets.map((preset) => preset.max)),
    };

    checkedPresets.forEach((preset) => {
      this.selectedFilters.set(this.valueKey(filterId, `price-bucket-${preset.key}`), {
        filter: { price: combined },

        filterId,

        valueId: `price-bucket-${preset.key}`,

        urlParam: "price",

        urlValue: preset.key,
      });
    });
  }

  render(filters) {
    const openFilterIds = new Set(
      Array.from(this.root.querySelectorAll("[data-api-filter] details[open]"))
        .map((details) => details.closest("[data-api-filter]")?.dataset.filterId)
        .filter(Boolean),
    );

    const colorFamilyFilter = filters.find((filter) => this.isColorFamilyFilter(filter));

    const colorFilter = filters.find((filter) => this.isColorFilter(filter));

    let colorHierarchyRendered = false;

    const descriptors = [];

    filters.forEach((filter) => {

      const isHierarchyFilter = filter === colorFamilyFilter || filter === colorFilter;

      if (isHierarchyFilter && colorFamilyFilter && colorFilter) {
        if (this.searchQuery) {

          if (colorHierarchyRendered) {
            return;
          }

          colorHierarchyRendered = true;

          descriptors.push({
            groupKey: "color-family",
            render: (index) =>
              this.renderColorHierarchy(
                colorFamilyFilter,
                colorFilter,
                index,
                openFilterIds.has(colorFamilyFilter.id) || openFilterIds.has(colorFilter.id),
              ),
          });

          return;
        }

        if (colorHierarchyRendered) {
          return;
        }

        colorHierarchyRendered = true;

        descriptors.push({
          groupKey: "color",
          render: (index) =>
            this.renderColorHierarchy(
              colorFamilyFilter,
              colorFilter,
              index,
              openFilterIds.has(colorFamilyFilter.id) || openFilterIds.has(colorFilter.id),
            ),
        });

        return;
      }

      if (this.isPriceRangeFilter(filter)) {
        // No price range has products: drop the whole Price group.
        if (!this.visiblePriceBucketPresets(filter).length) {
          return;
        }

        descriptors.push({
          groupKey: "price",
          render: (index) => this.renderPriceRangeFilter(filter, index, openFilterIds.has(filter.id)),
        });

        return;
      }

      const groupKey = this.filterGroupKey(filter);

      descriptors.push({
        groupKey,
        render: (index) => this.renderFilter(filter, index, openFilterIds.has(filter.id)),
      });
    });

    if (this.searchQuery) {
      const orderIndex = (groupKey) => {
        const position = SEARCH_FILTER_GROUP_ORDER.indexOf(groupKey);

        return position === -1 ? SEARCH_FILTER_GROUP_ORDER.length : position;
      };

      descriptors.sort((a, b) => orderIndex(a.groupKey) - orderIndex(b.groupKey));
    }

    this.root.innerHTML = descriptors.map((descriptor, index) => descriptor.render(index)).join("");

    this.bindEvents();

    this.bindPriceRangeEvents();

    this.renderClearAllControls();

    this.renderShopByColor(filters);
  }

  renderShopByColor(filters) {

    if (!this.shopByColorRoot || !document.documentElement.contains(this.shopByColorRoot)) {
      this.shopByColorRoot =
        this.collectionRoot?.querySelector("[data-shop-by-color]") ||
        document.querySelector("[data-shop-by-color]") ||
        null;
    }

    if (!this.shopByColorRoot) {
      console.warn("[SHOP BY COLOR] Render target missing.");

      return;
    }

    const familyFilter = filters.find((filter) => this.isColorFamilyFilter(filter));

    const colorFilter = filters.find((filter) => this.isColorFilter(filter));

    if (!familyFilter) {
      this.shopByColorRoot.innerHTML = "";

      this.shopByColorRoot.hidden = true;

      return;
    }

    const selectedFamily = this.getSelectedValue(familyFilter);

    const selectedColor = colorFilter ? this.getSelectedValue(colorFilter) : null;

    let body = "";

    if (selectedFamily && colorFilter && this.familySubcolorValues(colorFilter).length) {
      body = this.renderShopBySecondaryColors(familyFilter, colorFilter, selectedFamily, selectedColor);
    } else {

      body = this.renderShopByColorFamilies(familyFilter, selectedFamily);
    }

    this.shopByColorRoot.innerHTML = `
      <section
        class="shop-by-color"
        data-shop-by-color-rendered
      >
        <div
          class="shop-by-color__header"
        >
          <h3
            class="shop-by-color__title"
          >
            Shop by Color
          </h3>
        </div>

        ${body}
      </section>
    `;

    this.shopByColorRoot.hidden = false;

    this.shopByColorRoot.removeAttribute("hidden");

    this.bindShopByColorEvents();
  }

  wrapShopByColorScroller(listHtml) {
    return `
      <div class="shop-by-color__scroller-wrapper">
        <button
          type="button"
          class="shop-by-color__nav shop-by-color__nav--prev"
          data-shop-by-color-nav="prev"
          aria-label="Scroll to previous colors"
          hidden
        >
          <svg
            aria-hidden="true"
            focusable="false"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path d="M14 8L10 12L14 16" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div
          class="shop-by-color__scroller"
          data-shop-by-color-scroller
        >
          ${listHtml}
        </div>

        <button
          type="button"
          class="shop-by-color__nav shop-by-color__nav--next"
          data-shop-by-color-nav="next"
          aria-label="Scroll to more colors"
          hidden
        >
          <svg
            aria-hidden="true"
            focusable="false"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path d="M10 8L14 12L10 16" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;
  }

  renderShopByColorFamilies(filter, selectedFamily = null) {
    const values = (filter.values || []).filter((value) => {
      const selected = this.selectedFilters.has(this.valueKey(filter.id, value.id));

      return value.count > 0 || selected;
    });

    const list = `
      <ul
        class="shop-by-color__list"
        role="list"
      >
        ${values.map((value) => this.renderShopByColorFamilyValue(filter, value, selectedFamily)).join("")}
      </ul>
    `;

    return this.wrapShopByColorScroller(list);
  }

  renderShopByColorFamilyValue(filter, value, selectedFamily = null) {
    const key = this.valueKey(filter.id, value.id);

    const checked = this.selectedFilters.has(key) || selectedFamily?.id === value.id;

    const imageUrl = this.getValueImageUrl(value) || this.getValueSwatchImageUrl(value);

    const swatchColor = value?.swatch?.color || null;

    const filterInput = this.escapeAttribute(value.input);

    const urlParam = this.filterParamName(filter);

    const urlValue = this.slugify(value.label);

    let visual = `
      <span
        class="
          shop-by-color__visual
          shop-by-color__visual--placeholder
        "
        aria-hidden="true"
      ></span>
    `;

    if (imageUrl) {
      visual = `
        <span
          class="shop-by-color__visual"
        >
          <img
            class="shop-by-color__image"
            src="${this.escapeAttribute(imageUrl)}"
            alt=""
            loading="lazy"
          >
        </span>
      `;
    } else if (swatchColor) {
      visual = `
        <span
          class="
            shop-by-color__visual
            shop-by-color__visual--color
          "
          style="
            --shop-by-color-swatch:
            ${this.escapeAttribute(swatchColor)};
          "
          aria-hidden="true"
        ></span>
      `;
    }

    return `
      <li
        class="shop-by-color__item"
      >
        <label
          class="
            shop-by-color__option
            ${checked ? "is-active" : ""}
          "
        >
          <input
            type="radio"
            name="shop-by-color-family"
            class="visually-hidden"

            data-shop-by-color-input

            data-filter-id="${this.escapeAttribute(filter.id)}"

            data-value-id="${this.escapeAttribute(value.id)}"

            data-filter-input="${filterInput}"

            data-url-param="${this.escapeAttribute(urlParam)}"

            data-url-value="${this.escapeAttribute(urlValue)}"

            ${checked ? "checked" : ""}
          >

          ${visual}

          <span
            class="
              shop-by-color__label
              body-small
            "
          >
            ${this.escapeHtml(value.label)}
          </span>
        </label>
      </li>
    `;
  }

  renderShopBySecondaryColors(familyFilter, colorFilter, selectedFamily, selectedColor = null) {
    const values = this.familySubcolorValues(colorFilter);

    const list = `
      <ul
        class="
          shop-by-color__list
          shop-by-color__list--secondary
        "
        role="list"
      >

        <li
          class="shop-by-color__item"
        >
          <button
            type="button"
            class="shop-by-color__all-colors"
            data-shop-by-color-back
          >
            <span
              class="shop-by-color__back-icon"
              aria-hidden="true"
            >
              <svg
            aria-hidden="true"
            focusable="false"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path d="M14 8L10 12L14 16" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
            </span>

            <span
              class="
                shop-by-color__label
                body
              "
            >
              All colors
            </span>
          </button>
        </li>

        ${values.map((value) => this.renderShopByColorSecondaryValue(colorFilter, value, selectedColor)).join("")}

      </ul>
    `;

    return this.wrapShopByColorScroller(list);
  }

  renderShopByColorSecondaryValue(filter, value, selectedColor = null) {
    const key = this.valueKey(filter.id, value.id);

    const checked = this.selectedFilters.has(key) || selectedColor?.id === value.id;

    const disabled = value.count === 0 && !checked;

    const imageUrl = this.getValueSwatchImageUrl(value) || this.getValueImageUrl(value);

    const swatchColor = value?.swatch?.color || null;

    const filterInput = this.escapeAttribute(value.input);

    const urlParam = this.filterParamName(filter);

    const urlValue = this.slugify(value.label);

    let visual = `
      <span
        class="
          shop-by-color__visual
          shop-by-color__visual--placeholder
        "
        aria-hidden="true"
      ></span>
    `;

    if (imageUrl) {
      visual = `
        <span
          class="shop-by-color__visual"
        >
          <img
            class="shop-by-color__image"
            src="${this.escapeAttribute(imageUrl)}"
            alt=""
            loading="lazy"
          >
        </span>
      `;
    } else if (swatchColor) {
      visual = `
        <span
          class="
            shop-by-color__visual
            shop-by-color__visual--color
          "
          style="
            --shop-by-color-swatch:
            ${this.escapeAttribute(swatchColor)};
          "
          aria-hidden="true"
        ></span>
      `;
    }

    return `
      <li
        class="shop-by-color__item"
      >
        <label
          class="
            shop-by-color__option
            shop-by-color__option--secondary

            ${checked ? "is-active" : ""}

            ${disabled ? "is-disabled" : ""}
          "
        >
          <input
            type="checkbox"
            class="visually-hidden"

            data-shop-by-color-input

            data-filter-id="${this.escapeAttribute(filter.id)}"

            data-value-id="${this.escapeAttribute(value.id)}"

            data-filter-input="${filterInput}"

            data-url-param="${this.escapeAttribute(urlParam)}"

            data-url-value="${this.escapeAttribute(urlValue)}"

            ${checked ? "checked" : ""}

            ${disabled ? "disabled" : ""}
          >

          ${visual}

          <span
            class="
              shop-by-color__label
              body-small
            "
          >
            ${this.escapeHtml(value.label)}
          </span>

          ${
            checked
              ? `
                <span
                  class="shop-by-color__remove"
                  aria-hidden="true"
                >
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path d="M16 8L8 16" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M16 16L8 8" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              `
              : ""
          }

        </label>
      </li>
    `;
  }

  bindShopByColorEvents() {
    if (!this.shopByColorRoot) {
      return;
    }

    this.shopByColorRoot.querySelectorAll("[data-shop-by-color-input]").forEach((input) => {
      input.addEventListener("change", async (event) => {
        event.stopPropagation();

        await this.handleChange(event.currentTarget);
      });
    });

    this.shopByColorRoot.querySelectorAll("[data-shop-by-color-back]").forEach((button) => {
      button.addEventListener("click", async (event) => {
        event.preventDefault();
        event.stopPropagation();

        await this.clearColorHierarchy();
      });
    });

    this.initShopByColorScrollNav();
  }

  initShopByColorScrollNav() {
    this.shopByColorRoot.querySelectorAll("[data-shop-by-color-scroller]").forEach((scroller) => {
      const wrapper = scroller.closest(".shop-by-color__scroller-wrapper");
      const prevButton = wrapper?.querySelector('[data-shop-by-color-nav="prev"]');
      const nextButton = wrapper?.querySelector('[data-shop-by-color-nav="next"]');

      if (!prevButton || !nextButton) {
        return;
      }

      const updateNav = () => {
        const canScroll = scroller.scrollWidth > scroller.clientWidth + 1;

        prevButton.hidden = !canScroll;
        nextButton.hidden = !canScroll;

        if (!canScroll) {
          return;
        }

        prevButton.disabled = scroller.scrollLeft <= 0;
        nextButton.disabled = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 1;
      };

      prevButton.addEventListener("click", () => {
        scroller.scrollBy({ left: -scroller.clientWidth * 0.8, behavior: "smooth" });
      });

      nextButton.addEventListener("click", () => {
        scroller.scrollBy({ left: scroller.clientWidth * 0.8, behavior: "smooth" });
      });

      scroller.addEventListener("scroll", updateNav, { passive: true });

      updateNav();
    });
  }

  renderColorHierarchy(familyFilter, colorFilter, index, shouldOpen = false) {
    const selectedFamily = this.getSelectedValue(familyFilter);

    const selectedColor = this.getSelectedValue(colorFilter);

    const selectedCount = Number(Boolean(selectedFamily)) + Number(Boolean(selectedColor));

    shouldOpen = shouldOpen || selectedCount > 0;

    const body = selectedFamily
      ? this.renderColorFamilySelected(familyFilter, colorFilter, selectedFamily)
      : this.renderColorFamilies(familyFilter);

    const groupKey = this.filterGroupKey(familyFilter);
    const headingLabel = this.searchQuery ? "Color Family" : "Color";

    return `
      <div
        class="
          facets__filter-accordion
          facets__filter-accordion--${groupKey}
          facets__color-hierarchy
          js-filter
        "
        data-api-filter
        data-filter-id="${this.escapeAttribute(familyFilter.id)}"
        data-filter-type="${this.escapeAttribute(groupKey)}"
        data-index="${index}"
      >
        <conditional-details
          class="facets__accordion"
        >
          <details
            class="facets__accordion-details"
            id="ApiFacet-${index}"
            ${shouldOpen ? "open" : ""}
          >
            <summary
              class="
                facets__accordion-summary
                facets__item-heading
              "
            >
              <span
                class="
                  facets__item-heading-label
                  label
                "
              >
                ${headingLabel}

                ${selectedCount > 0 ? ` (${selectedCount})` : ""}
              </span>

              <span
                class="
                  facets__accordion-icon-wrapper
                "
              >
                <svg
                  class="facets__accordion-icon"
                  viewBox="0 0 12 8"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    fill="none"
                    stroke="currentColor"
                  />
                </svg>
              </span>
            </summary>

            <div
              class="facets__item-options"
              data-content
            >
              <fieldset>
                <legend
                  class="visually-hidden"
                >
                  ${headingLabel}
                </legend>

                ${body}
              </fieldset>
            </div>
          </details>
        </conditional-details>
      </div>
    `;
  }

  renderColorFamilies(filter) {
    const values = (filter.values || []).filter(
      (value) => value.count > 0 || this.selectedFilters.has(this.valueKey(filter.id, value.id)),
    );

    return `
      <ul
        class="
          facets__filter-list
          facets__filter-list--color-family
        "
        role="list"
      >
        ${values.map((value) => this.renderColorFamilyValue(filter, value)).join("")}
      </ul>
    `;
  }

  renderColorFamilyValue(filter, value, pinned = false) {
    const key = this.valueKey(filter.id, value.id);

    const checked = this.selectedFilters.has(key);

    const imageUrl = this.getValueImageUrl(value) || this.getValueSwatchImageUrl(value);

    const swatchColor = value?.swatch?.color || null;

    const filterInput = this.escapeAttribute(value.input);

    const urlParam = this.filterParamName(filter);

    const urlValue = this.slugify(value.label);

    let visual = "";

    if (imageUrl) {
      visual = `
        <span
          class="facets__color-family-visual"
        >
          <img
            class="facets__color-family-image"
            src="${this.escapeAttribute(imageUrl)}"
            alt=""
            loading="lazy"
          >
        </span>
      `;
    } else if (swatchColor) {
      visual = `
        <span
          class="
            facets__color-family-visual
            facets__color-family-visual--color
          "
          style="
            --color-family-swatch:
            ${this.escapeAttribute(swatchColor)};
          "
          aria-hidden="true"
        ></span>
      `;
    }

    return `
      <li
        class="
          facets__filter-list-item
          facets__color-family-item
        "
      >
        <label
          class="
            facets__color-family-option
            ${checked ? "is-active" : ""}
          "
        >
          <input
            type="radio"
            name="api-color-family"

            class="
              facets__facet-option-input
              facets__color-family-input
            "

            data-api-filter-input
            ${pinned ? "data-color-family-pinned" : ""}

            data-filter-id="${this.escapeAttribute(filter.id)}"

            data-value-id="${this.escapeAttribute(value.id)}"

            data-filter-input="${filterInput}"

            data-url-param="${this.escapeAttribute(urlParam)}"

            data-url-value="${this.escapeAttribute(urlValue)}"

            ${checked ? "checked" : ""}
          >

          ${visual}

          <span
            class="
              facets__color-family-label
              body
            "
          >
            ${this.escapeHtml(value.label)}
          </span>

          <span
            class="visually-hidden"
          >
            ${this.escapeHtml(value.label)}
            (${value.count})
          </span>
        </label>
      </li>
    `;
  }

  renderColorFamilySelected(familyFilter, colorFilter, selectedFamily) {
    const subcolorValues = colorFilter ? this.familySubcolorValues(colorFilter) : [];

    return `
      <ul
        class="
          facets__filter-list
          facets__filter-list--color-family
        "
        role="list"
      >
        ${this.renderColorFamilyValue(familyFilter, selectedFamily, true)}

        ${subcolorValues
          .map((value) => this.renderValue(colorFilter, value, "facets__filter-list-item--indented"))
          .join("")}
      </ul>
    `;
  }

  renderPriceRangeFilter(filter, index, shouldOpen = false) {
    const selectedCount = PRICE_RANGE_PRESETS.filter((preset) =>
      this.selectedFilters.has(this.valueKey(filter.id, `price-bucket-${preset.key}`)),
    ).length;

    shouldOpen = shouldOpen || selectedCount > 0;

    return `
      <div
        class="
          facets__filter-accordion
          facets__filter-accordion--price
          js-filter
        "
        data-api-filter
        data-filter-id="${this.escapeAttribute(filter.id)}"
        data-filter-type="price"
        data-index="${index}"
      >
        <conditional-details
          class="facets__accordion"
        >
          <details
            class="facets__accordion-details"
            id="ApiFacet-${index}"
            ${shouldOpen ? "open" : ""}
          >
            <summary
              class="
                facets__accordion-summary
                facets__item-heading
              "
            >
              <span
                class="
                  facets__item-heading-label
                  label
                "
              >
                ${this.escapeHtml(filter.label || "Price")}

                ${selectedCount > 0 ? ` (${selectedCount})` : ""}
              </span>

              <span
                class="
                  facets__accordion-icon-wrapper
                "
              >
                <svg
                  class="facets__accordion-icon"
                  viewBox="0 0 12 8"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    fill="none"
                    stroke="currentColor"
                  />
                </svg>
              </span>
            </summary>

            <div
              class="facets__item-options"
              data-content
            >
              <fieldset>
                <legend
                  class="visually-hidden"
                >
                  ${this.escapeHtml(filter.label || "Price")}
                </legend>

                <ul
                  class="facets__filter-list"
                  data-price-range
                  data-filter-id="${this.escapeAttribute(filter.id)}"
                  role="list"
                >
                  ${this.visiblePriceBucketPresets(filter)
                    .map((preset) => this.renderPriceBucketValue(filter, preset))
                    .join("")}
                </ul>
              </fieldset>
            </div>
          </details>
        </conditional-details>
      </div>
    `;
  }

  renderPriceBucketValue(filter, preset) {
    const checked = this.selectedFilters.has(this.valueKey(filter.id, `price-bucket-${preset.key}`));

    return `
      <li
        class="facets__filter-list-item"
      >
        <label
          class="
            facets__facet-option
            checkbox-label

            ${checked ? "is-active" : ""}
          "
        >
          <div
            class="checkbox-wrapper"
          >
            <input
              type="checkbox"
              class="facets__facet-option-input"

              data-price-bucket-input

              data-filter-id="${this.escapeAttribute(filter.id)}"

              data-bucket-key="${this.escapeAttribute(preset.key)}"

              ${checked ? "checked" : ""}
            >

            <span
              class="checkbox-icon"
            ></span>
          </div>

          <span
            class="
              facets__facet-option-text
              body
            "
          >
            ${this.escapeHtml(preset.label)}
          </span>
        </label>
      </li>
    `;
  }

  renderFilter(filter, index, shouldOpen = false) {
    const values = filter.values || [];

    if (!values.length) {
      return "";
    }

    const selectedCount = this.selectedCountForFilter(filter);

    shouldOpen = shouldOpen || selectedCount > 0;

    const presentation = String(filter.presentation || "").toUpperCase();

    const groupKey = this.filterGroupKey(filter);

    return `
      <div
        class="
          facets__filter-accordion
          ${groupKey ? `facets__filter-accordion--${groupKey}` : ""}
          js-filter
        "
        data-api-filter
        data-filter-id="${this.escapeAttribute(filter.id)}"
        data-filter-type="${this.escapeAttribute(groupKey)}"
        data-index="${index}"
      >
        <conditional-details
          class="facets__accordion"
        >
          <details
            class="facets__accordion-details"
            id="ApiFacet-${index}"
            ${shouldOpen ? "open" : ""}
          >
            <summary
              class="
                facets__accordion-summary
                facets__item-heading
              "
            >
              <span
                class="
                  facets__item-heading-label
                  label
                "
              >
                ${this.escapeHtml(filter.label)}

                ${selectedCount > 0 ? ` (${selectedCount})` : ""}
              </span>

              <span
                class="
                  facets__accordion-icon-wrapper
                "
              >
                <svg
                  class="facets__accordion-icon"
                  viewBox="0 0 12 8"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    fill="none"
                    stroke="currentColor"
                  />
                </svg>
              </span>
            </summary>

            <div
              class="facets__item-options"
              data-content
            >
              <fieldset>
                <legend
                  class="visually-hidden"
                >
                  ${this.escapeHtml(filter.label)}
                </legend>

                <ul
                  class="
                    facets__filter-list

                    ${presentation === "SWATCH" || presentation === "IMAGE" ? "facets__filter-list--swatch" : ""}
                  "
                  role="list"
                >
                  ${values.map((value) => this.renderValue(filter, value)).join("")}
                </ul>
              </fieldset>
            </div>
          </details>
        </conditional-details>
      </div>
    `;
  }

  renderValue(filter, value, extraItemClass = "") {
    const key = this.valueKey(filter.id, value.id);

    const checked = this.selectedFilters.has(key);

    const disabled = value.count === 0 && !checked;

    const presentation = String(filter.presentation || "").toUpperCase();

    const isSwatch = presentation === "SWATCH";

    const isImage = presentation === "IMAGE";

    const isVisual = isSwatch || isImage;

    const filterInput = this.escapeAttribute(value.input);

    const urlParam = this.filterParamName(filter);

    const urlValue = this.slugify(value.label);

    const commonAttributes = `
      data-api-filter-input

      data-filter-id="${this.escapeAttribute(filter.id)}"

      data-value-id="${this.escapeAttribute(value.id)}"

      data-filter-input="${filterInput}"

      data-url-param="${this.escapeAttribute(urlParam)}"

      data-url-value="${this.escapeAttribute(urlValue)}"

      ${checked ? "checked" : ""}

      ${disabled ? "disabled" : ""}
    `;

    let controlMarkup = "";

    if (isVisual) {
      const swatchImage = isSwatch ? this.getValueSwatchImageUrl(value) : this.getValueImageUrl(value);

      const swatchColor = isSwatch ? value?.swatch?.color || null : null;

      let swatchContent = "";

      if (swatchImage) {
        swatchContent = `
          <img
            class="facets__filter-swatch-image"
            src="${this.escapeAttribute(swatchImage)}"
            alt=""
            loading="lazy"
          >
        `;
      } else if (swatchColor) {
        swatchContent = `
          <span
            class="facets__filter-swatch-color"
            style="
              --swatch-color:
              ${this.escapeAttribute(swatchColor)};
            "
          ></span>
        `;
      }

      controlMarkup = `
        <input
          type="checkbox"

          class="
            facets__facet-option-input
            facets__filter-swatch-input
          "

          ${commonAttributes}
        >

        <span
          class="
            facets__filter-swatch
            ${isImage ? "facets__filter-swatch--image" : ""}
          "
          data-api-swatch
        >
          <span
            class="
              facets__filter-swatch-inner

              ${swatchContent ? "" : "facets__filter-swatch-inner--placeholder"}
            "
          >
            ${swatchContent}
          </span>
        </span>
      `;
    } else {
      controlMarkup = `
        <div
          class="checkbox-wrapper"
        >
          <input
            type="checkbox"
            class="facets__facet-option-input"
            ${commonAttributes}
          >

          <span
            class="checkbox-icon"
          ></span>
        </div>
      `;
    }

    return `
      <li
        class="
          facets__filter-list-item
          ${extraItemClass}
        "
      >
        <label
          class="
            facets__facet-option

            ${disabled ? "facets__facet-option--no-products" : ""}

            ${isVisual ? "facets__facet-option--swatch" : "checkbox-label"}

            ${checked ? "is-active" : ""}
          "
        >
          ${controlMarkup}

          <span
            class="
              facets__facet-option-text
              body
            "
            aria-hidden="true"
          >
            ${this.escapeHtml(value.label)}
          </span>

          <span
            class="visually-hidden"
          >
            ${this.escapeHtml(value.label)}
            (${value.count})
          </span>
        </label>
      </li>
    `;
  }

  bindEvents() {
    this.root.querySelectorAll("[data-api-filter-input]").forEach((input) => {
      input.addEventListener("change", async (event) => {
        event.stopPropagation();

        await this.handleChange(event.currentTarget);
      });
    });

    this.root.querySelectorAll("[data-color-family-pinned]").forEach((input) => {
      input.addEventListener("click", async (event) => {
        if (!input.defaultChecked) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        await this.clearColorHierarchy(true);
      });
    });
  }

  bindPriceRangeEvents() {
    this.root.querySelectorAll("[data-price-range]").forEach((wrapper) => {
      wrapper.querySelectorAll("[data-price-bucket-input]").forEach((input) => {
        input.addEventListener("change", async (event) => {
          event.stopPropagation();

          await this.handlePriceBucketChange(wrapper);
        });
      });
    });
  }

  async handleChange(input) {
    const filterId = input.dataset.filterId;

    const valueId = input.dataset.valueId;

    const key = this.valueKey(filterId, valueId);

    let parsed;

    try {
      parsed = JSON.parse(input.dataset.filterInput);
    } catch (error) {
      console.error("[API FACETS] Invalid filter input:", input.dataset.filterInput);

      return;
    }

    const selectingColorFamily = input.checked && this.isColorFamilyFilterId(filterId);

    const selectingColor = input.checked && (this.isColorInput(parsed) || this.isColorFilterId(filterId));

    if (selectingColorFamily) {
      Array.from(this.selectedFilters.entries()).forEach(([existingKey, entry]) => {
        if (this.isColorFamilyEntry(entry) || this.isColorEntry(entry)) {
          this.selectedFilters.delete(existingKey);
        }
      });
    } else if (selectingColor) {

      Array.from(this.selectedFilters.entries()).forEach(([existingKey, entry]) => {
        if (this.isColorEntry(entry)) {
          this.selectedFilters.delete(existingKey);
        }
      });
    }

    if (input.checked) {

      const filter = this.searchQuery ? this.availableFilters.find((candidate) => candidate.id === filterId) : null;

      const value = filter?.values?.find((candidate) => candidate.id === valueId);

      this.selectedFilters.set(key, {
        filter: parsed,

        filterId,

        valueId,

        urlParam: input.dataset.urlParam,

        urlValue: input.dataset.urlValue,

        queryGroup: filter ? this.buildSearchFilterQueryGroup(filter) : null,

        queryClause: filter ? this.buildSearchFilterQueryClause(filter, value, parsed) : null,
      });
    } else {
      this.selectedFilters.delete(key);
    }

    if (input.hasAttribute("data-api-filter-input") && this.isMobileDrawer()) {
      this.render(this.availableFilters);

      return;
    }

    this.updateUrl();

    await this.load();
  }

  async handlePriceBucketChange(wrapper) {
    const filterId = wrapper.dataset.filterId;

    const checkedKeys = Array.from(wrapper.querySelectorAll("[data-price-bucket-input]:checked")).map(
      (input) => input.dataset.bucketKey,
    );

    this.applyPriceBuckets(filterId, checkedKeys);

    if (this.isMobileDrawer()) {
      this.render(this.availableFilters);

      return;
    }

    this.updateUrl();

    await this.load();
  }

  async clearColorHierarchy(fromDrawer = false) {
    Array.from(this.selectedFilters.entries()).forEach(([key, entry]) => {
      if (this.isColorFamilyEntry(entry) || this.isColorEntry(entry)) {
        this.selectedFilters.delete(key);
      }
    });

    if (fromDrawer && this.isMobileDrawer()) {
      this.render(this.availableFilters);

      return;
    }

    this.updateUrl();

    await this.load();
  }

  getOwnedUrlParams() {
    const names = new Set();

    this.availableFilters.forEach((filter) => {
      const param = this.filterParamName(filter);

      if (param) {
        names.add(param);
      }
    });

    if (this.searchQuery) {
      SEARCH_URL_PARAM_NAMES.forEach((name) => names.add(name));
    }

    this.selectedFilters.forEach((entry) => {
      if (entry.urlParam) {
        names.add(entry.urlParam);
      }
    });

    return names;
  }

  updateUrl() {
    const params = new URLSearchParams(window.location.search);

    this.getOwnedUrlParams().forEach((param) => {
      params.delete(param);
    });

    Array.from(params.keys()).forEach((key) => {
      if (key.startsWith("plp_filter.")) {
        params.delete(key);
      }
    });

    const appended = this.searchQuery ? new Set() : null;

    this.selectedFilters.forEach((entry) => {
      if (!entry.urlParam || !entry.urlValue) {
        return;
      }

      if (this.isColorFamilyEntry(entry) || this.isColorEntry(entry)) {
        params.set(entry.urlParam, entry.urlValue);

        return;
      }

      if (appended) {
        const dedupeKey = `${entry.urlParam}::${entry.urlValue}`;

        if (appended.has(dedupeKey)) {
          return;
        }

        appended.add(dedupeKey);
      }

      params.append(entry.urlParam, entry.urlValue);
    });

    params.delete("page");
    params.delete("grouped_page");

    const query = params.toString();

    const url = `${window.location.pathname}` + `${query ? `?${query}` : ""}` + `${window.location.hash}`;

    history.replaceState(history.state, "", url);
  }

  clearAll() {
    this.selectedFilters.clear();

    if (this.isMobileDrawer()) {
      this.render(this.availableFilters);

      return;
    }

    this.updateUrl();

    this.load();
  }

  renderClearAllControls() {
    const hasActiveFilters = this.selectedFilters.size > 0;

    const html = hasActiveFilters
      ? `
          <button
            type="button"
            class="
              facets__clear-all
              link
              link--x-small
            "
            data-api-clear-all
          >
            <span>
              Clear All Filters
            </span>
            <svg
              aria-hidden="true"
              focusable="false"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path d="M16 8L8 16" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16 16L8 8" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        `
      : "";

    const desktopTarget = document.querySelector('[data-render="clear-all-top-bar"]');

    const drawerTarget = document.querySelector('[data-render="clear-all-drawer-header"]');

    if (desktopTarget) {
      desktopTarget.innerHTML = html;

      desktopTarget.hidden = !hasActiveFilters;
    }

    if (drawerTarget) {
      drawerTarget.innerHTML = html;
    }

    document.querySelectorAll("[data-api-clear-all]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.clearAll();
      });
    });
  }

  dispatchProducts(products, { isBackgroundCompletion = false, requestToken = null } = {}) {
    const orderedNodes = this.applyColorFamilyOrder(products.nodes || [], this.availableFilters);

    const activeColorFamily = this.getActiveColorFamily(this.availableFilters);

    const specificColorSelected = this.hasSpecificColorSelected(this.availableFilters);

    const selectedFilters = Array.from(this.selectedFilters.entries()).map(([key, entry]) => ({
      key,

      value: entry.filter,

      filterId: entry.filterId,

      valueId: entry.valueId,

      urlParam: entry.urlParam,

      urlValue: entry.urlValue,
    }));

    return new Promise((resolveRender) => {
      this.root.dispatchEvent(
        new CustomEvent("api-facets:products", {
          bubbles: true,

          detail: {
          products: orderedNodes,

          pageInfo: products.pageInfo || null,

          filters: products.filters || [],

          selectedFilters,

          storefrontFilters: this.getFiltersArray(),

          // Collections: price is selected but filtered client-side, so it
          // isn't in storefrontFilters — the count must scan like search.
          clientPriceFilter: this.clientPriceFiltering && this.hasActivePriceSelection(),

          hasActiveFilters: this.selectedFilters.size > 0,

          colorActive: this.hasActiveColorFilter(),

          activeColorFamily,

          specificColorSelected,

          isBackgroundCompletion,
          requestToken,
          resolveRender,
          },
        }),
      );
    });
  }
}

export function initializeApiFacets(container = document) {
  const readyPromises = [];

  container.querySelectorAll("[data-api-filters]").forEach((root) => {
    if (root.dataset.apiFacetsInitialized === "true") {
      if (root._apiFacets?.ready) readyPromises.push(root._apiFacets.ready);
      return;
    }

    root.dataset.apiFacetsInitialized = "true";
    root._apiFacets = new ApiFacets(root);
    readyPromises.push(root._apiFacets.ready);
  });

  return Promise.allSettled(readyPromises);
}
